import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { useAuth } from '../hooks/useAuth';

const Collections = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hash = searchParams.get('hash');

  useEffect(() => {
    const handleRedirect = async () => {
      // Wait for auth to load
      if (authLoading) {
        return;
      }

      // If not authenticated, component will show NoAccess
      if (!isAuthenticated) {
        return;
      }

      // Check if hash parameter exists
      if (!hash) {
        toast.error('Invalid QR code: missing hash parameter');
        return;
      }

      // Make authenticated API call to redirect endpoint
      setIsRedirecting(true);
      try {
        const response = await fetchWithAuth(`/qr_code_redirect?hash=${hash}`);

        if (response.ok) {
          const data = await response.json();
          if (data.order_id) {
            navigate(`/orders/${data.order_id}`);
          } else {
            toast.error('Invalid response from server');
          }
        } else if (response.status === 404) {
          toast.error('Order not found');
        } else {
          const data = await response.json();
          toast.error(data.error || 'Failed to process QR code');
        }
      } catch (error) {
        console.error('QR redirect error:', error);
        toast.error('Failed to process QR code');
      } finally {
        setIsRedirecting(false);
      }
    };

    handleRedirect();
  }, [hash, isAuthenticated, authLoading, navigate]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <img src="/tini-logo.png" alt="Tini Express" className="h-16 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to public site if not authenticated
  if (!isAuthenticated) {
    window.location.href = 'https://tini-express.com';
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <img src="/tini-logo.png" alt="Tini Express" className="h-16 mx-auto mb-4" />
          <p className="text-gray-600">Redirecting...</p>
        </div>
      </div>
    );
  }

  // Show loading while redirecting
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <img src="/tini-logo.png" alt="Tini Express" className="h-16 mx-auto mb-4" />
        <p className="text-gray-600">
          {isRedirecting ? 'Processing QR code...' : 'Redirecting...'}
        </p>
      </div>
    </div>
  );
};

export default Collections;
